import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { toLegalRoleLabels, toRoleFlag } from "@/shared/utils/syncRoleColumns";
import { logActivity } from "@/shared/utils/activityLogger";
import { useAppContext } from "./useAppContext";
import { normalizeNameFields } from "@/shared/utils/names";

import type { Database } from "@/shared/types/database.types";

// Helper to check if admin is currently simulating (can't use hook inside queryFn)
const isAdminSimulating = () => {
  try {
    return !!localStorage.getItem("admin_simulation");
  } catch {
    return false;
  }
};

// Emails that should never have a "Me" contact auto-created
// This should match ALLOWED_SIMULATION_EMAILS in useAdminSimulation.ts
const SKIP_AUTO_CONTACT_EMAILS = [
  "care@elayne.com",
  "careteam@elayne.com",
  "dani+212614012026@elayne.com",
  "dani+214014012026@elayne.com",
];

const shouldSkipAutoContact = (email: string) => {
  if (isAdminSimulating()) return true;
  const lowerEmail = email.toLowerCase();
  // Skip if email is in the list or contains "careteam"
  return SKIP_AUTO_CONTACT_EMAILS.includes(lowerEmail) || lowerEmail.includes("careteam");
};
type UserContactInsert = Database["public"]["Tables"]["estate_contacts"]["Insert"];
type UserContactUpdate = Database["public"]["Tables"]["estate_contacts"]["Update"];

export function useUserContacts() {
  const { user, person } = useAppContext();
  const queryClient = useQueryClient();

  const {
    data: contacts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["person-contacts", user?.last_active_estate_id, user?.email],
    staleTime: 0, // Always consider data stale to ensure fresh data on navigation
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: "always", // Always refetch when component mounts, regardless of stale time
    refetchInterval: 10000, // Poll every 10 seconds while on the page
    queryFn: async () => {
      if (!user?.last_active_estate_id || !user?.email) return [];

      const { data, error } = await supabase
        .from("estate_contacts")
        .select(
          `
          *,
          source_document:estate_documents!source_document_id(
            id,
            file_name,
            display_name,
            document_type,
            created_at
          )
        `,
        )
        .eq("estate_id", user.last_active_estate_id)
        .eq("is_hidden", false)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Check if "Me" contact exists
      const meContact = data?.find((contact) => contact.email === user.email);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      const [{ data: userPersonData }, { data: userRecord }, estateRelationshipsResult] =
        await Promise.all([
          supabase
            .from("user_estates")
            .select(
              "relationship_to_decedent, is_nominated, is_executor, is_administrator, is_trustee",
            )
            .eq("user_id", user.user_id)
            .eq("estate_id", user.last_active_estate_id)
            .maybeSingle(),
          supabase.from("users").select("phone_number").eq("user_id", user.user_id).single(),
          session?.access_token
            ? supabase.functions.invoke("account/estate-relationships", {
                body: { estate_id: user.last_active_estate_id },
                headers: { Authorization: `Bearer ${session.access_token}` },
              })
            : Promise.resolve({ data: null, error: null }),
        ]);

      const allUserEstates = (estateRelationshipsResult?.data?.relationships ?? []) as Array<{
        user_id: number;
        relationship_to_decedent: string;
      }>;

      const userPersonRelationship = userPersonData?.relationship_to_decedent || null;
      const legalRoles = toLegalRoleLabels({
        is_nominated: userPersonData?.is_nominated ?? false,
        is_executor: userPersonData?.is_executor ?? false,
        is_administrator: userPersonData?.is_administrator ?? false,
        is_trustee: userPersonData?.is_trustee ?? false,
      });

      // If "Me" contact exists but has wrong user_id, update it
      // This ensures "uploaded by" shows the correct contact name
      if (meContact && meContact.user_id !== user.user_id) {
        await supabase
          .from("estate_contacts")
          .update({ user_id: user.user_id })
          .eq("id", meContact.id);

        // Update the local data to reflect the change
        meContact.user_id = user.user_id;
      }

      // Sync relationship, legal_role, and phone from user_estates to the "Me" contact
      if (meContact) {
        const needsRelationship =
          (meContact.relationship || null) !== (userPersonRelationship || null);
        // Only roles with a user_estates flag are synced; witness, successor, etc. stay as set
        const meRoles = [
          ...(meContact.legal_role ?? []).filter((role: string) => !toRoleFlag(role)),
          ...legalRoles,
        ];
        const currentRoles = [...(meContact.legal_role ?? [])].sort();
        const expectedRoles = [...meRoles].sort();
        const needsRoles = JSON.stringify(currentRoles) !== JSON.stringify(expectedRoles);
        const userPhone = userRecord?.phone_number || null;
        const needsPhone = (meContact.phone || null) !== (userPhone || null);

        if (needsRelationship || needsRoles || needsPhone) {
          const updates: Record<string, string | string[] | null> = {};
          if (needsRelationship) updates.relationship = userPersonRelationship;
          if (needsRoles) updates.legal_role = meRoles.length > 0 ? meRoles : null;
          if (needsPhone) updates.phone = userPhone;

          await supabase.from("estate_contacts").update(updates).eq("id", meContact.id);

          if (needsRelationship) meContact.relationship = userPersonRelationship;
          if (needsRoles) meContact.legal_role = meRoles.length > 0 ? meRoles : null;
          if (needsPhone) meContact.phone = userPhone;
        }
      }

      // For invited contacts, prefer user_estates.relationship_to_decedent over estate_contacts.relationship
      if (allUserEstates?.length) {
        const ueByUserId = new Map(
          allUserEstates
            .filter((ue) => ue.user_id && ue.relationship_to_decedent)
            .map((ue) => [ue.user_id, ue.relationship_to_decedent]),
        );
        for (const contact of data ?? []) {
          if (contact.user_id && contact.user_id !== user.user_id) {
            const ueRelationship = ueByUserId.get(contact.user_id);
            if (ueRelationship) contact.relationship = ueRelationship;
          }
        }
      }

      if (!meContact && !shouldSkipAutoContact(user.email)) {
        // Create "Me" contact if it doesn't exist (skip for careteam/admin emails)
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("last_active_estate_id, first_name, last_name, full_name")
          .eq("user_id", user.user_id)
          .single();

        if (userError) throw userError;
        if (!userData?.last_active_estate_id) {
          throw new Error("No active person found for user");
        }

        // Parse the full_name to get first and last names for the contact
        let contactFirstName = userData.first_name || user.email.split("@")[0];
        let contactLastName = userData.last_name || "";

        if (userData.full_name) {
          const nameParts = userData.full_name.trim().split(" ");
          contactFirstName = nameParts[0] || contactFirstName;
          contactLastName = nameParts.slice(1).join(" ") || contactLastName;
        }

        const meContactData = {
          user_id: user.user_id,
          estate_id: user.last_active_estate_id,
          first_name: contactFirstName,
          last_name: contactLastName,
          email: user.email,
          phone: userRecord?.phone_number || null,
          invited: false,
          relationship: userPersonRelationship,
          legal_role: legalRoles.length > 0 ? legalRoles : null,
        };

        const { data: newMeContact, error: createError } = await supabase
          .from("estate_contacts")
          .insert(meContactData)
          .select()
          .single();

        if (createError) throw createError;

        // Add the new "Me" contact to the beginning of the list
        return [newMeContact, ...(data || [])];
      }

      return data || [];
    },
    enabled: !!person?.estate_id && !!user?.email,
  });

  // Get account owner information via edge function (bypasses RLS)
  const { data: accountOwner, isLoading: isLoadingOwner } = useQuery({
    queryKey: ["account-owner", person?.estate_id],
    queryFn: async () => {
      if (!person?.estate_id) return null;

      // Get session to ensure we have auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.error("[get-account-owner] No active session");
        return null;
      }

      const { data, error } = await supabase.functions.invoke("account/owner", {
        body: { estate_id: person.estate_id },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        // Try to get more details from the error
        console.error("[get-account-owner] Failed:", error);
        console.error("[get-account-owner] Error context:", error.context);
        return null;
      }

      if (data?.error) {
        console.error("[get-account-owner] Error in response:", data.error);
        return null;
      }

      return data?.owner || null;
    },
    enabled: !!person?.estate_id,
  });

  const createContact = useMutation({
    mutationFn: async (contact: Omit<UserContactInsert, "user_id" | "estate_id">) => {
      if (!user?.user_id) throw new Error("No user found");

      // Get the user's last_active_estate_id
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("last_active_estate_id")
        .eq("user_id", user.user_id)
        .single();

      if (userError) throw userError;
      if (!userData?.last_active_estate_id) {
        throw new Error("No active person found for user");
      }

      const normalizedEmail = contact.email?.toLowerCase() || null;

      if (normalizedEmail) {
        const { data: existingContact, error: checkError } = await supabase
          .from("estate_contacts")
          .select("id, email")
          .eq("estate_id", userData.last_active_estate_id)
          .eq("email", normalizedEmail)
          .single();

        if (checkError && checkError.code !== "PGRST116") {
          throw checkError;
        }

        if (existingContact) {
          throw new Error("A contact with this email already exists");
        }
      }

      const { data, error } = await supabase
        .from("estate_contacts")
        .insert({
          ...normalizeNameFields(contact),
          email: normalizedEmail,
          user_id: user.user_id,
          estate_id: userData.last_active_estate_id,
        })
        .select()
        .single();

      if (error) throw error;

      await logActivity({
        estate_id: userData.last_active_estate_id,
        action_type: "added_contact",
        user_id: user.user_id,
        entity_type: "contact",
        entity_id: String(data.id),
        metadata: {
          name: [contact.first_name, contact.last_name].filter(Boolean).join(" "),
          relationship: contact.relationship ?? null,
          email: contact.email ?? null,
          legal_role: contact.legal_role ?? null,
        },
      });

      return data;
    },
    // Returned so mutateAsync resolves after the refetch and callers can select the new contact
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["person-contacts", person?.estate_id],
      }),
  });

  const updateContact = useMutation({
    mutationFn: async ({ id, ...updates }: UserContactUpdate & { id: number }) => {
      if (updates.email !== undefined) {
        updates.email = updates.email?.toLowerCase() || null;
      }

      const { data, error } = await supabase
        .from("estate_contacts")
        .update(normalizeNameFields(updates))
        .eq("id", id)
        .eq("estate_id", person?.estate_id)
        .select()
        .single();

      if (error) throw error;

      if (person?.estate_id && user?.user_id) {
        logActivity({
          estate_id: person.estate_id,
          action_type: "updated_contact",
          user_id: user.user_id,
          entity_type: "contact",
          entity_id: String(id),
          metadata: { ...updates },
        }).catch(() => {});
      }

      return data;
    },
    onMutate: async ({ id, ...updates }) => {
      const queryKey = ["person-contacts", person?.estate_id];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueriesData<Array<{ id: number }>>({ queryKey });
      queryClient.setQueriesData<Array<{ id: number }>>({ queryKey }, (old) =>
        old?.map((contact) => (contact.id === id ? { ...contact, ...updates } : contact)),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      context?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["person-contacts", person?.estate_id],
      });
    },
  });

  const deleteContact = useMutation({
    mutationFn: async (id: number) => {
      const { data: contactSnapshot } = await supabase
        .from("estate_contacts")
        .select("first_name, last_name, relationship, email")
        .eq("id", id)
        .maybeSingle();

      const { error } = await supabase
        .from("estate_contacts")
        .delete()
        .eq("id", id)
        .eq("estate_id", person?.estate_id);

      if (error) throw error;

      if (person?.estate_id && user?.user_id) {
        await logActivity({
          estate_id: person.estate_id,
          action_type: "deleted_contact",
          user_id: user.user_id,
          entity_type: "contact",
          entity_id: String(id),
          metadata: {
            name: contactSnapshot
              ? [contactSnapshot.first_name, contactSnapshot.last_name].filter(Boolean).join(" ") ||
                null
              : null,
            relationship: contactSnapshot?.relationship ?? null,
            email: contactSnapshot?.email ?? null,
          },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["person-contacts", person?.estate_id],
      });
    },
  });

  const inviteContact = useMutation({
    mutationFn: async (contactId: number) => {
      const { data, error } = await supabase
        .from("estate_contacts")
        .update({ invited: true })
        .eq("id", contactId)
        .eq("estate_id", person?.estate_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["person-contacts", person?.estate_id],
      });
    },
  });

  // Picking someone from People updates them; otherwise the person is new.
  const saveContactAsync = (
    id: number | null,
    contact: Omit<UserContactInsert, "user_id" | "estate_id">,
  ) => (id ? updateContact.mutateAsync({ id, ...contact }) : createContact.mutateAsync(contact));

  return {
    contacts,
    isLoading: isLoading || isLoadingOwner,
    error,
    accountOwner,
    createContact: createContact.mutate,
    createContactAsync: createContact.mutateAsync,
    updateContact: updateContact.mutate,
    updateContactAsync: updateContact.mutateAsync,
    saveContactAsync,
    deleteContact: deleteContact.mutate,
    inviteContact: inviteContact.mutate,
    isCreating: createContact.isPending,
    isUpdating: updateContact.isPending,
    isDeleting: deleteContact.isPending,
    isInviting: inviteContact.isPending,
  };
}
